import {
  isScheduleNode,
  isTeamNode,
} from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'

export interface DivisionOption {
  scheduleId: string
  name: string
  /**
   * Ancestors between the root and this division, joined for display — e.g.
   * "East › Pool B". Empty when the division sits directly under the season.
   */
  path: string
  /**
   * Teams DIRECTLY in this division — the size of the list a move would land in.
   * Nested divisions are not counted; their teams belong to their own option.
   */
  teamCount: number
}

const PATH_SEPARATOR = ' › '

/**
 * Every DIVISION in the tree, each carrying the path that leads to it.
 *
 * Distinct from `collectDestinations`, which answers "what can hold teams" and
 * so also returns a childless conference. Only a division can actually receive
 * a team here, and two divisions in different conferences frequently share a
 * name — hence the path, which is what makes them tellable apart in a flat
 * list.
 *
 * The ROOT is excluded from the path: it is identical for every option, so it
 * would add width without adding information.
 */
export const collectDivisionOptions = (
  nodes: OperationsNode[],
  trail: string[] = [],
  isRoot = true
): DivisionOption[] =>
  nodes.flatMap((node) => {
    if (!isScheduleNode(node)) {
      return []
    }

    // The root's own name never enters the trail passed to its children.
    const childTrail = isRoot ? trail : [...trail, node.name]

    if (node.type === 'division') {
      return [
        {
          scheduleId: node.scheduleId,
          name: node.name,
          path: trail.join(PATH_SEPARATOR),
          teamCount: node.children.filter(isTeamNode).length,
        },
        // A division may still nest divisions beneath it.
        ...collectDivisionOptions(node.children, childTrail, false),
      ]
    }

    return collectDivisionOptions(node.children, childTrail, false)
  })
